import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../config";
import "./EmployeeProjects.css";

const EmployeeProjects = ({ employee }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingTaskId, setUpdatingTaskId] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [activeTab, setActiveTab] = useState("active");

useEffect(() => {
  const fetchTasks = async () => {
    try {
      setLoading(true);
      // Try by emp_id first (more reliable)
      let res;
      
      if (employee.emp_id) {
        try {
          console.log("Fetching tasks by emp_id:", employee.emp_id);
          res = await axios.get(
            `${API_BASE_URL}/api/tasks/emp_id/${employee.emp_id}`
          );
        } catch (empIdErr) {
          console.log("Emp_id endpoint failed, trying MongoDB ID:", employee._id);
          res = await axios.get(
            `${API_BASE_URL}/api/tasks/employee/${employee._id}`
          );
        }
      } else {
        res = await axios.get(
          `${API_BASE_URL}/api/tasks/employee/${employee._id}`
        );
      }
      
      console.log("API Response:", res.data);
      
      // FIX HERE: Check response structure
      if (res.data && res.data.success && Array.isArray(res.data.data)) {
        setTasks(res.data.data);  // ✅ CORRECT: Use res.data.data
      } else if (Array.isArray(res.data)) {
        setTasks(res.data);  // Fallback for old API structure
      } else {
        console.error("Unexpected response format:", res.data);
        setTasks([]);
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };
  
  if (employee?._id || employee?.emp_id) {
    fetchTasks();
  }
}, [employee]);

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case "high":
        return "#ff4757";
      case "medium":
        return "#ffa502";
      case "low":
        return "#2ed573";
      default:
        return "#57606f";
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "#2ed573";
      case "in progress":
        return "#1e90ff";
      case "not started":
        return "#ffa502";
      default:
        return "#57606f";
    }
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      await axios.patch(`${API_BASE_URL}/api/tasks/${taskId}/status`, {
        status: newStatus,
      });
      setTasks((prev) =>
        prev.map((task) =>
          task._id === taskId ? { ...task, status: newStatus } : task
        )
      );
      setUpdatingTaskId(null);
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update task status");
    }
  };

  const openNotesModal = (task) => {
    setSelectedTask(task);
    setNoteText("");
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;

    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/tasks/${selectedTask._id}/notes`,
        { text: noteText }
      );

      setTasks((prev) =>
        prev.map((t) => (t._id === selectedTask._id ? res.data : t))
      );

      setSelectedTask(res.data);
      setNoteText("");
    } catch (err) {
      console.error("Error adding note:", err);
      alert("Failed to add note");
    }
  };

  const activeTasks = tasks.filter(
    (t) => t.status?.toLowerCase() !== "completed"
  );
  const completedTasks = tasks.filter(
    (t) => t.status?.toLowerCase() === "completed"
  );

  if (loading) {
    return (
      <div className="employee-dashboard-pro">
        <div className="dashboard-loading-pro">
          <div className="loading-animation-pro">
            <div className="pulse-dot-pro dot-1-pro"></div>
            <div className="pulse-dot-pro dot-2-pro"></div>
            <div className="pulse-dot-pro dot-3-pro"></div>
          </div>
          <p className="gradient-text-pro">Loading your projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="employee-dashboard-pro">
      {/* Animated SVG Background */}
      <div className="animated-svg-background-pro">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="grad1-pro" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#667eea" stopOpacity="0.1" />
              <stop offset="100%" stopColor="#764ba2" stopOpacity="0.1" />
            </linearGradient>
            <pattern id="grid-pro" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grad1-pro)" />
          <rect width="100%" height="100%" fill="url(#grid-pro)" />
          
          {/* Animated Shapes */}
          <circle cx="10%" cy="20%" r="40" fill="#667eea" opacity="0.1" className="float-anim-pro">
            <animate attributeName="cy" values="20;25;20" dur="8s" repeatCount="indefinite" />
          </circle>
          <circle cx="90%" cy="80%" r="60" fill="#764ba2" opacity="0.1" className="float-anim-pro">
            <animate attributeName="cy" values="80;75;80" dur="10s" repeatCount="indefinite" />
          </circle>
          <rect x="85%" y="10%" width="80" height="80" fill="#f093fb" opacity="0.1" className="rotate-anim-pro">
            <animate attributeName="y" values="10;15;10" dur="12s" repeatCount="indefinite" />
          </rect>
        </svg>
      </div>

      {/* Header Section */}
      <div className="dashboard-header-pro">
        <div className="header-content-pro">
          <h1 className="dashboard-title-pro gradient-text-pro">
            Project Dashboard
          </h1>
          <p className="dashboard-subtitle-pro">
            Welcome back! Here's your current workload overview.
          </p>
        </div>
        
        <div className="stats-container-pro">
          <div className="stat-card-pro">
            <div className="stat-icon-pro">📊</div>
            <div className="stat-info-pro">
              <span className="stat-number-pro">{tasks.length}</span>
              <span className="stat-label-pro">Total Tasks</span>
            </div>
          </div>
          <div className="stat-card-pro">
            <div className="stat-icon-pro">✅</div>
            <div className="stat-info-pro">
              <span className="stat-number-pro">{completedTasks.length}</span>
              <span className="stat-label-pro">Completed</span>
            </div>
          </div>
          <div className="stat-card-pro">
            <div className="stat-icon-pro">🔄</div>
            <div className="stat-info-pro">
              <span className="stat-number-pro">
                {tasks.filter(t => t.status?.toLowerCase() === "in progress").length}
              </span>
              <span className="stat-label-pro">In Progress</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation-pro">
        <button 
          className={`tab-button-pro ${activeTab === "active" ? "tab-active-pro" : ""}`}
          onClick={() => setActiveTab("active")}
        >
          Active Tasks ({activeTasks.length})
        </button>
        <button 
          className={`tab-button-pro ${activeTab === "completed" ? "tab-active-pro" : ""}`}
          onClick={() => setActiveTab("completed")}
        >
          Completed ({completedTasks.length})
        </button>
      </div>

      {/* Active Tasks */}
      {activeTab === "active" && (
        <div className="tasks-section-pro">
          {activeTasks.length === 0 ? (
            <div className="empty-state-pro">
              <div className="empty-icon-pro">🎯</div>
              <h3 className="gradient-text-pro">No Active Tasks</h3>
              <p>All caught up! Completed tasks are waiting in the completed tab.</p>
            </div>
          ) : (
            <div className="tasks-grid-pro">
              {activeTasks.map((task, index) => (
                <div 
                  key={task._id} 
                  className="task-card-pro"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="task-card-header-pro">
                    <div className="task-title-section-pro">
                      <h3 className="task-title-pro">{task.title}</h3>
                      <div className="task-meta-pro">
                        <span className="task-date-pro">
                          Due: {new Date(task.dueDate).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="task-badges-pro">
                      <span
                        className="priority-badge-pro"
                        style={{ backgroundColor: getPriorityColor(task.priority) }}
                      >
                        {task.priority}
                      </span>
                      <span
                        className="status-badge-pro"
                        style={{ backgroundColor: getStatusColor(task.status) }}
                      >
                        {task.status}
                      </span>
                    </div>
                  </div>

                  <div className="task-content-pro">
                    {task.description && (
                      <p className="task-description-pro">{task.description}</p>
                    )}
                    
                    <div className="task-details-pro">
                      <div className="detail-item-pro">
                        <span className="detail-label-pro">👤 Assigned By:</span>
                        <span className="detail-value-pro">
                          {task.managerId?.name || "N/A"}
                        </span>
                      </div>
                      
                      {task.dueDate && (
                        <div className="detail-item-pro">
                          <span className="detail-label-pro">📅 Due Date:</span>
                          <span className={`detail-value-pro ${new Date(task.dueDate) < new Date() ? "overdue-pro" : ""}`}>
                            {new Date(task.dueDate).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>

                    {task.extraFields && task.extraFields.length > 0 && (
                      <div className="extra-fields-pro">
                        <h4 className="extra-fields-title-pro">Additional Information</h4>
                        <div className="extra-fields-grid-pro">
                          {task.extraFields.map((field, index) => (
                            <div key={index} className="extra-field-item-pro">
                              <span className="field-label-pro">{field.label}:</span>
                              {field.type === "file" ? (
                                <a
                                  href={field.value}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="file-link-pro"
                                >
                                  📎 Download
                                </a>
                              ) : (
                                <span className="field-value-pro">
                                  {field.value || "-"}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="task-footer-pro">
                    <div className="task-actions-pro">
                      {updatingTaskId === task._id ? (
                        <select
                          className="status-select-pro"
                          value={task.status}
                          onChange={(e) => handleUpdateStatus(task._id, e.target.value)}
                        >
                          <option value="Not Started">Not Started</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                        </select>
                      ) : (
                        <button
                          className="action-btn-pro update-btn-pro"
                          onClick={() => setUpdatingTaskId(task._id)}
                        >
                          Update Status
                        </button>
                      )}
                      
                      <button
                        className="action-btn-pro notes-btn-pro"
                        onClick={() => openNotesModal(task)}
                      >
                        📝 Notes
                      </button>
                      
                      <button className="action-btn-pro view-btn-pro">
                        👁️ View
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Completed Tasks */}
      {activeTab === "completed" && completedTasks.length > 0 && (
        <div className="completed-section-pro">
          <div className="completed-grid-pro">
            {completedTasks.map((task, index) => (
              <div 
                key={task._id} 
                className="completed-card-pro"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="completed-header-pro">
                  <h4 className="completed-title-pro">{task.title}</h4>
                  <span className="completion-date-pro">
                    {new Date(task.updatedAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="completed-details-pro">
                  <span className="completed-by-pro">
                    By: {task.managerId?.name || "N/A"}
                  </span>
                  <span className="completed-priority-pro">
                    Priority: {task.priority}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Enhanced Notes Modal */}
      {selectedTask && (
        <div className="notes-modal-pro">
          <div className="modal-overlay-pro" onClick={() => setSelectedTask(null)}></div>
          <div className="modal-container-pro">
            <div className="modal-header-pro">
              <h3 className="modal-title-pro">Progress Notes</h3>
              <span className="modal-subtitle-pro">{selectedTask.title}</span>
              <button 
                className="modal-close-pro"
                onClick={() => setSelectedTask(null)}
              >
                ✕
              </button>
            </div>
            
            <div className="notes-content-pro">
              <div className="notes-list-pro">
                {selectedTask.notes?.length > 0 ? (
                  selectedTask.notes.map((note, index) => (
                    <div key={index} className="note-item-pro">
                      <div className="note-bubble-pro">
                        <p className="note-text-pro">{note.text}</p>
                        <span className="note-time-pro">
                          {new Date(note.createdAt).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-notes-pro">
                    <div className="no-notes-icon-pro">💬</div>
                    <p>No notes yet. Add your first progress update!</p>
                  </div>
                )}
              </div>
              
              <div className="note-input-section-pro">
                <textarea
                  className="note-textarea-pro"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add your progress note, update, or comment..."
                  rows="4"
                />
                <button 
                  className="save-note-btn-pro"
                  onClick={handleAddNote}
                  disabled={!noteText.trim()}
                >
                  💾 Save Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeProjects;